import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import type { PlanEvent } from './PlanEventSheet';

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['calendars', 'events'] });
  void qc.invalidateQueries({ queryKey: ['calendars', 'busy'] });
}

export function useToggleEventDone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, done }: { eventId: number; done: boolean }) =>
      api<PlanEvent>(`/api/calendars/events/${eventId}/done`, {
        method: 'PATCH',
        json: { done },
      }),
    onSuccess: () => invalidate(qc),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

/**
 * Delete an event here and upstream.
 *
 * The server decides whether this calendar may be written to; the UI greys the
 * action out to match, but the refusal message is the server's, because it is
 * the one that knows.
 */
export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: number) =>
      api<void>(`/api/calendars/events/${eventId}`, { method: 'DELETE' }),
    onSuccess: () => invalidate(qc),
    onError: (err: ApiError) => toast.error(err.message),
  });
}
